<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent, ref, watch, onUnmounted } from 'vue';
import type { ICellEditorParams } from 'ag-grid-community';

const INVALID_CELL_CLASS = 'ag-number-editor-cell--invalid';

type NumberEditorParams = ICellEditorParams & {
    onInputValidator?: (value: number | null, params: ICellEditorParams) => boolean;
};

export default defineComponent({
    props: {
        params: {
            type: Object as PropType<NumberEditorParams>,
            required: true,
        },
    },
    setup(props) {
        const initialValue = typeof props.params.value === 'number' ? props.params.value : null;
        const value = ref<number | null>(initialValue);
        const inputRef = ref<HTMLInputElement | null>(null);

        // Input-time validity — recomputed on each keystroke to toggle the red styling only.
        const invalid = computed((): boolean => {
            const validator = props.params.onInputValidator;
            return validator != null && !validator(value.value, props.params);
        });

        // Reflect validity as a class on the cell element (not the input) so the invalid styling —
        // red value text and red focus border — lives in the global grid stylesheet. Setting the
        // theme's border variable on the cell cascades down to the focused input's border/glow.
        watch(invalid, (isInvalid) => {
            props.params.eGridCell.classList.toggle(INVALID_CELL_CLASS, isInvalid);
        }, { immediate: true });

        // The cell element is reused after the editor is torn down — drop the class so a rejected,
        // reverted value doesn't stay red.
        onUnmounted(() => {
            props.params.eGridCell.classList.remove(INVALID_CELL_CLASS);
        });

        function getValue(): number | null {
            return value.value;
        }

        function afterGuiAttached(): void {
            inputRef.value?.focus();
        }

        function onInput(event: Event): void {
            if (!(event.target instanceof HTMLInputElement)) return;
            const raw = event.target.valueAsNumber;
            value.value = Number.isNaN(raw) ? null : raw;
        }

        return {
            value,
            inputRef,
            getValue,
            afterGuiAttached,
            onInput,
        };
    },
});
</script>

<template>
    <input
        ref="inputRef"
        type="number"
        :value="value ?? ''"
        class="ag-number-editor-input"
        @input="onInput"
    />
</template>

<style scoped>
.ag-number-editor-input {
    display: block;
    height: 100% !important;
}
</style>
